import React, { useEffect, useState } from "react";
import { Zap, Compass, ArrowRight, Shield } from "lucide-react";
import { shojinApi } from "../../lib/shojinApi";
import { trackEvent } from "../../lib/analytics";
import ShowUpProtocolModal from "./ShowUpProtocolModal";

/**
 * The Shōjin Dō presence inside the Daily Call.
 *
 * Weaves three integral surfaces into a single strip that lives at the top
 * of the DailyCallCard (never a separate destination):
 *   1. Comeback banner — auto-shows when the user returns after a missed day.
 *   2. Identity anchor — the user's own "I am someone who ___" line, or an
 *      inline prompt to set one if empty.
 *   3. Practice cue + Show-up Protocol button.
 */
export default function ShojinStrip({ onCommit }) {
  const [identity, setIdentity] = useState("");
  const [cue, setCue] = useState(null);
  const [comeback, setComeback] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [protocolOpen, setProtocolOpen] = useState(false);
  const [resilience, setResilience] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [c, cb, r] = await Promise.all([
        shojinApi.dailyCue(),
        shojinApi.comeback().catch(() => ({ triggered: false })),
        shojinApi.resilience(30).catch(() => null),
      ]);
      setCue(c || null);
      setIdentity(c?.identity_statement || "");
      setDraft(c?.identity_statement || "");
      setComeback(cb?.triggered ? cb : null);
      setResilience(r || null);
    } catch { /* keep silent — this is enrichment, not core */ }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const saveIdentity = async () => {
    const value = (draft || "").trim();
    if (value.length < 3) return;
    try {
      await shojinApi.setIdentity(value);
      trackEvent("shojin_identity_set", { length: value.length });
      setIdentity(value);
      setEditing(false);
    } catch { /* noop */ }
  };

  if (loading) return null;

  return (
    <>
      {/* Comeback banner — priority position when triggered */}
      {comeback && (
        <div
          data-testid="shojin-comeback-banner"
          className="mb-3 rounded-xl border border-amber-400/30 bg-gradient-to-br from-amber-400/10 to-transparent p-4"
        >
          <p className="text-[10px] uppercase tracking-[0.16em] font-black text-amber-300">Shōjin Dō · Comeback</p>
          <p className="mt-1 text-white text-sm font-bold">{comeback.script.no_shame_line}</p>
          <p className="mt-1 text-zinc-400 text-xs">
            {comeback.days_missed === 1 ? "1 day off" : `${comeback.days_missed} days off`}. Do the smallest possible session today — that IS the training.
          </p>
          <p className="mt-2 text-[11px] text-[#BEFF38] italic">"{comeback.script.identity_reanchor}"</p>
        </div>
      )}

      {/* Identity + Cue strip */}
      <div
        data-testid="shojin-strip"
        className="mb-3 rounded-xl border border-[#BEFF38]/20 bg-gradient-to-br from-[#BEFF38]/5 to-transparent p-4 relative overflow-hidden"
      >
        {/* Subtle enso mark bottom-right — the artwork whispers, not shouts */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-6 -bottom-6 w-24 h-24 opacity-[0.08]"
          style={{
            backgroundImage: 'url(/shojin/shojin-do.png)',
            backgroundSize: '260% auto',
            backgroundPosition: '18% 28%',
            filter: 'invert(1) grayscale(1) brightness(1.4)',
          }}
        />
        <div className="relative">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0">
            <Compass className="w-3.5 h-3.5 text-[#BEFF38] shrink-0 mt-0.5" strokeWidth={2.5} />
            <p className="text-[10px] uppercase tracking-[0.16em] font-black text-[#BEFF38]">
              Shōjin Dō · 精進道
            </p>
          </div>
          {resilience && resilience.total_sessions > 0 && (
            <div
              data-testid="shojin-resilience-pill"
              title={`${resilience.total_sessions} sessions in 30 days · ${resilience.hard_sessions} on hard days (mood/stress/sleep flagged)`}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#ff7a1a]/30 bg-[#ff7a1a]/10 px-2 py-0.5"
            >
              <Shield className="w-3 h-3 text-[#ff7a1a]" strokeWidth={2.5} />
              <span className="text-[10px] font-black text-white tabular-nums">{resilience.score}</span>
              {resilience.hard_sessions > 0 && (
                <span className="text-[9px] font-black text-[#ff7a1a] tabular-nums">· {resilience.hard_sessions} hard</span>
              )}
            </div>
          )}
        </div>

        {!identity && !editing && (
          <button
            onClick={() => setEditing(true)}
            data-testid="shojin-set-identity"
            className="mt-2 text-left w-full group"
          >
            <p className="text-white text-sm leading-snug">
              Complete once: <span className="text-zinc-500 group-hover:text-zinc-300 transition-colors italic">"I am someone who ______"</span>
            </p>
            <p className="mt-1 text-[11px] text-zinc-500 group-hover:text-[#BEFF38] transition-colors">Tap to write yours →</p>
          </button>
        )}

        {editing && (
          <div className="mt-2" data-testid="shojin-identity-editor">
            <div className="flex items-center gap-1 text-sm text-white">
              <span className="text-zinc-500 italic">I am someone who</span>
            </div>
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, 140))}
              onKeyDown={(e) => { if (e.key === "Enter") saveIdentity(); }}
              placeholder="trains, regardless of how I feel"
              data-testid="shojin-identity-input"
              className="mt-1.5 w-full bg-[#0b0c10] border border-white/10 focus:border-[#BEFF38] rounded-lg px-3 py-2 text-white text-sm outline-none"
            />
            <div className="mt-2 flex gap-2">
              <button
                onClick={saveIdentity}
                disabled={(draft || "").trim().length < 3}
                data-testid="shojin-identity-save"
                className="flex-1 bg-[#BEFF38] text-black font-black uppercase tracking-wider text-[11px] py-2 rounded-md hover:brightness-95 disabled:opacity-40"
              >
                Anchor it
              </button>
              <button
                onClick={() => { setEditing(false); setDraft(identity); }}
                data-testid="shojin-identity-cancel"
                className="text-zinc-500 hover:text-white text-[11px] font-black uppercase tracking-wider px-3"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {identity && !editing && (
          <>
            <button
              onClick={() => setEditing(true)}
              data-testid="shojin-identity-display"
              className="mt-2 text-left w-full group"
            >
              <p className="text-white text-sm leading-snug">
                <span className="text-zinc-400">I am someone who</span>{" "}
                <span className="font-bold">{identity}</span>
              </p>
              <p className="mt-0.5 text-[10px] text-zinc-600 group-hover:text-zinc-400 transition-colors">Tap to edit</p>
            </button>

            {cue?.cue && (
              <div className="mt-3 pt-3 border-t border-white/5">
                <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Today's practice cue · {cue.level_name}</p>
                <p className="mt-1 text-zinc-300 text-xs leading-relaxed">{cue.cue}</p>
              </div>
            )}
          </>
        )}

        {/* Show-up button — always available regardless of identity state */}
        <button
          onClick={() => { trackEvent("shojin_show_up_click"); setProtocolOpen(true); }}
          data-testid="shojin-show-up-btn"
          className="mt-3 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-[#BEFF38]/40 hover:bg-white/5 transition-colors group"
        >
          <span className="flex items-center gap-2 text-white text-xs font-bold">
            <Zap className="w-3.5 h-3.5 text-[#ff7a1a]" strokeWidth={2.5} />
            Don't feel like training?
          </span>
          <span className="text-[10px] uppercase tracking-wider font-black text-zinc-500 group-hover:text-[#BEFF38] flex items-center gap-1">
            90-sec protocol <ArrowRight className="w-3 h-3" />
          </span>
        </button>
        </div>
      </div>

      <ShowUpProtocolModal
        open={protocolOpen}
        onClose={() => setProtocolOpen(false)}
        onCommit={() => onCommit && onCommit()}
      />
    </>
  );
}
